import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr, runJob } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Upload, Loader2, Wand2, Download, Trash2, ScanLine, ImageOff } from "lucide-react";

const MAX_SIDE = 2048;

function shrink(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s);
      c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.92));
    };
    img.onerror = () => reject(new Error("Could not read that image"));
    img.src = URL.createObjectURL(file);
  });
}

async function download(url, name) {
  const res = await fetch(url);
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
}

function Pane({ label, src, testid, children }) {
  return (
    <div className={`${ui.card} overflow-hidden`} data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-slate-800 text-[11px] font-mono uppercase tracking-widest text-slate-400">{label}</div>
      <div className="aspect-[16/9] bg-slate-950 flex items-center justify-center">
        {src ? <img src={src} alt={label} className="w-full h-full object-contain" /> : children}
      </div>
    </div>
  );
}

export default function StreetViewCleaner() {
  const [input, setInput] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/streetview/history").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setInput(await shrink(f)); setResult(null); } catch (err) { toast.error(err.message); }
  };

  const clean = async () => {
    if (!input) return toast.error("Upload a Street View screenshot first");
    setBusy(true);
    try {
      const data = await runJob("/streetview/clean", { image_base64: input });
      setResult(data);
      loadHistory();
      toast.success("Image cleaned");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/images/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      if (result?.id === id) setResult(null);
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="streetview-page">
      <div>
        <h1 className={ui.pageHeader}>Street View Cleaner</h1>
        <p className={`${ui.muted} mt-1`}>Upload a Google Street View screenshot. The cleaner removes the logos, arrows, labels and other overlays so it looks like a normal phone photo.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className={`${ui.btnGhost} cursor-pointer`}>
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="streetview-upload" />
          <Upload className="w-4 h-4 text-indigo-400" /> Upload Street View Screenshot
        </label>
        <button data-testid="streetview-clean-btn" onClick={clean} disabled={busy || !input} className={ui.btnPrimary}>
          {busy ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4" />} {busy ? "Cleaning… (~30–90s)" : "Clean Image"}
        </button>
        {result && (
          <button data-testid="streetview-download-btn" onClick={() => download(result.url, `streetview-clean-${result.id}.png`)} className={ui.btnGhost}>
            <Download className="w-4 h-4 text-emerald-400" /> Download Clean Image
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Pane label="Before" src={input} testid="streetview-before">
          <span className="text-sm text-slate-600 flex items-center gap-2"><ScanLine className="w-4 h-4" /> No screenshot uploaded</span>
        </Pane>
        <Pane label="After" src={result?.url} testid="streetview-after">
          <span className="text-sm text-slate-600 flex items-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 spin" /> Cleaning…</> : <><ImageOff className="w-4 h-4" /> Cleaned image appears here</>}
          </span>
        </Pane>
      </div>

      <div>
        <h2 className={`${ui.sectionHeader} mb-3`}>History</h2>
        {history.length === 0 ? (
          <div className={`${ui.card} p-8 text-center text-sm text-slate-500`}>No cleaned images yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="streetview-history">
            {history.map((h) => (
              <div key={h.id} className={`${ui.card} overflow-hidden`} data-testid={`streetview-item-${h.id}`}>
                <div className="grid grid-cols-2 gap-px bg-slate-800">
                  <img src={h.original_url} alt="before" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  <img src={h.url} alt="after" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                </div>
                <div className="flex items-center justify-between p-3">
                  <span className="text-[11px] font-mono text-slate-500">{new Date(h.created_at).toLocaleString()}</span>
                  <div className="flex gap-1">
                    <button onClick={() => setResult(h) || setInput(h.original_url)} data-testid={`streetview-view-${h.id}`} className="px-2 py-1 text-xs text-slate-300 hover:text-indigo-300">View</button>
                    <button onClick={() => download(h.url, `streetview-clean-${h.id}.png`)} data-testid={`streetview-dl-${h.id}`} className="p-1.5 text-slate-400 hover:text-emerald-300" title="Download"><Download className="w-4 h-4" /></button>
                    <button onClick={() => del(h.id)} data-testid={`streetview-del-${h.id}`} className="p-1.5 text-slate-400 hover:text-rose-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
